import {
  type InvoicePDFInfoItemRow,
  type InvoicePDFColConfig,
  createDocumentDefinition,
  type InvoicePDFInfo,
} from './PdfInvoice'
import { createTableRow } from './PdfProject'
import { isInquiryStatusAfterOfferedCheck } from '@/pages/inquiries/composables'
import { type IInquiryProduct, type IInquiry } from '@/pages/inquiries/types'
import { translate as $t } from '@/plugins/translation'
import type { TableCell } from 'pdfmake/interfaces'
import { omit } from 'radash'

export const convertToPlain = (html: string) => {
  const tempDivElement = document.createElement('div')
  tempDivElement.innerHTML = html
  return tempDivElement.textContent || tempDivElement.innerText || ''
}

export const createTableRows = (items: IInquiryProduct[]) =>
  items.map<InvoicePDFInfoItemRow>((item, index) => createTableRow(item, '', index))

export const tableConfig: InvoicePDFColConfig[] = [
  {
    id: 'index',
    label: '#',
    width: 'auto',
    style: 'tableCellIndex',
  },
  {
    id: 'image',
    label: '',
    width: 'auto',
  },
  {
    id: 'product',
    label: $t('Product'),
    width: '*',
  },
  {
    id: 'quantity',
    label: $t('Quantity'),
    width: 'auto',
  },
  {
    id: 'retailPriceNetString',
    label: $t('retailPriceNetString'),
    width: 'auto',
    alignment: 'right',
  },
  {
    id: 'retailPriceTotalNetString',
    label: $t('retailPriceTotalNetString'),
    width: 'auto',
    style: 'tableCellBold',
    alignment: 'right',
  },
]

const footerItemsBeforeOffered = (inquiry: IInquiry) =>
  [
    [
      '',
      { text: `${$t('RRP sum net')}:`, bold: true },
      { text: inquiry.totalRRPNetString, bold: true, alignment: 'right', fontSize: 9 },
    ],
    [
      '',
      {
        text: `${$t('Total gross')}:`,
      },
      {
        stack: [{ text: inquiry.totalRRPGrossString }],
        alignment: 'right',
      },
    ],
    [
      '',
      {
        text: `${$t('19% vat')}:`,
        bold: false,
        lineHeight: 1.25,
      },
      {
        stack: [{ text: inquiry.rrpVatAmountString }],
        alignment: 'right',
      },
    ],
  ] as TableCell[][]

const footerItemsAfterOffered = (inquiry: IInquiry, hasReachedDiscountLimit: boolean) =>
  [
    [
      '',
      { text: `${$t('RRP sum net')}:`, bold: true },
      { text: inquiry.totalRRPNetString, bold: true, alignment: 'right' },
    ],
    inquiry.discountPercent !== '0 %'
      ? [
          '',
          `${$t('Project discount')}:`,
          {
            text: hasReachedDiscountLimit ? `(${inquiry.discountPercent}) -${inquiry.discountAbsolute} ` : '-',
            alignment: 'right',
          },
        ]
      : [],
    [
      '',
      { text: `${$t('Subtotal')}:`, bold: true },
      { text: inquiry.totalDiscountedPriceNetString, bold: true, alignment: 'right' },
    ],
    [
      '',
      `${$t('Delivery Costs')}:`,
      {
        text: `${inquiry.deliveryCostsString || '-'}`,
        alignment: 'right',
      },
    ],
    [
      '',
      {
        margin: [0, 7, 0, 0],
        text: `${$t('Project price')}:`,
        bold: true,
      },
      {
        margin: [0, 7, 0, 0],
        stack: [{ text: inquiry.totalNetString }],
        fontSize: 9,
        bold: true,
        alignment: 'right',
      },
    ],
    [
      '',
      {
        text: `${$t('Total gross incl. 19% vat ({0})', '', [inquiry.vatAmountString])}:`,
        bold: false,
        lineHeight: 1.25,
      },
      {
        stack: [{ text: inquiry.totalGrossString }],
        alignment: 'right',
      },
    ],
  ] as TableCell[][]

export const createInquiryPdfDefinition = async (inquiry: IInquiry, hideFields: string[]) => {
  const hasReachedDiscountLimit = inquiry.discountAbsolute !== '0,00 €' || inquiry.totalNet >= 1000
  const isInquiryStatusAfterOffered = isInquiryStatusAfterOfferedCheck(inquiry.status)

  const pdfInfoInquiry: InvoicePDFInfo = {
    info: {
      title: inquiry.inqTitle || inquiry.id,
    },
    logo: {
      svg: `<svg xmlns="http://www.w3.org/2000/svg" width="160" height="30" fill="none" viewBox="0 0 160 30"><path fill="#000" d="M10.247 2.487c2.848 0 5.242.918 7.185 2.753 2.04 1.949 3.062 4.28 3.062 6.991v14.576L15.803 29h-.247V12.232c0-1.868-.515-3.236-1.543-4.105-1.03-.869-2.284-1.304-3.766-1.304-1.482 0-2.737.435-3.765 1.304-1.03.869-1.544 2.237-1.544 4.105v14.576L.247 29H0V12.232c0-2.68 1.02-5.01 3.062-6.992 1.91-1.835 4.304-2.753 7.185-2.753Zm139.506 0c2.848 0 5.243.918 7.185 2.753C158.979 7.19 160 9.52 160 12.232v14.576L155.309 29h-.247V12.232c0-1.868-.515-3.236-1.543-4.105-1.029-.869-2.284-1.304-3.766-1.304-1.481 0-2.737.435-3.765 1.304-1.029.869-1.543 2.237-1.543 4.105v14.576L139.753 29h-.247V12.232c0-2.68 1.021-5.01 3.062-6.992 1.909-1.835 4.304-2.753 7.185-2.753ZM32.839 2v16.768c0 1.868.515 3.236 1.544 4.105 1.028.87 2.284 1.303 3.765 1.303 1.481 0 2.737-.434 3.765-1.303 1.029-.869 1.544-2.237 1.544-4.105V4.192L48.148 2h.247v16.768c0 2.68-1.02 5.01-3.062 6.992-1.91 1.835-4.305 2.753-7.185 2.753-2.848 0-5.243-.918-7.185-2.753-2.041-1.949-3.062-4.28-3.062-6.992V4.192L32.593 2h.246Zm27.902 0v16.768c0 1.868.514 3.236 1.543 4.105 1.028.87 2.284 1.303 3.765 1.303 1.482 0 2.737-.434 3.766-1.303 1.028-.869 1.543-2.237 1.543-4.105V4.192L76.049 2h.247v16.768c0 2.68-1.02 5.01-3.061 6.992-1.91 1.835-4.305 2.753-7.186 2.753-2.848 0-5.243-.918-7.185-2.753-2.041-1.949-3.061-4.28-3.061-6.992V4.192L60.493 2h.248Zm33.21.487c2.814 0 5.21.918 7.185 2.753 2.041 1.9 3.062 3.995 3.062 6.285v.049l-4.692 2.192h-.247v-1.534c0-1.868-.514-3.236-1.543-4.105-1.029-.869-2.284-1.304-3.765-1.304-1.482 0-2.737.435-3.765 1.304-1.03.869-1.544 2.237-1.544 4.105v6.536c0 1.868.514 3.236 1.544 4.105 1.028.87 2.283 1.303 3.765 1.303 1.481 0 2.736-.438 3.765-1.315s1.543-2.022 1.543-3.435l4.692-2.192h.247v1.534c0 2.68-1.021 5.01-3.062 6.992-1.91 1.836-4.305 2.753-7.185 2.753-2.848 0-5.243-.917-7.186-2.753-2.04-1.949-3.061-4.28-3.061-6.992v-6.536c0-2.696 1.02-5.027 3.061-6.992 1.91-1.835 4.305-2.753 7.186-2.753Zm38.148 10.46v5.821c0 2.68-1.021 5.01-3.062 6.992-1.909 1.835-4.305 2.753-7.185 2.753-2.848 0-5.243-.918-7.185-2.753-1.05-1.003-1.82-2.11-2.329-3.316l-.132-.331 4.194-1.957h.258c.201 1.173.675 2.08 1.429 2.717 1.028.869 2.283 1.303 3.765 1.303 1.481 0 2.736-.434 3.765-1.303.972-.82 1.485-2.086 1.539-3.798l.005-.307v-3.632l4.691-2.188h.247Zm-10.247-10.46c2.847 0 5.242.918 7.185 2.753 1.059 1.012 1.838 2.129 2.347 3.346l-4.168 1.948h-.247c-.231-1.006-.675-1.835-1.352-2.407-1.029-.869-2.284-1.303-3.765-1.303-1.482 0-2.737.434-3.766 1.303-1.028.869-1.543 2.237-1.543 4.105v3.278l-4.701 2.193h-.237v-5.471c0-2.68 1.02-5.01 3.061-6.992 1.91-1.835 4.305-2.752 7.186-2.752Z"/></svg>`,
      width: 80,
      alignment: 'right',
      margin: [0, 0, 0, 30],
    },
    titleContent: [
      { text: $t('Offer') + ': ', bold: true },
      { text: inquiry.inqTitle || inquiry.id },
      !isInquiryStatusAfterOffered ? `  (${$t('Inquiry not offered yet!')})` : '',
    ],
    addressContent: [
      {
        text: 'nuucon GmbH  |  Jarrestraße 42  |  22303 Hamburg  |  www.nuucon.com',
        fontSize: 6.5,
        color: '#666',
        margin: [0, 0, 0, 25],
      },
      ...(inquiry.invoiceAddress
        ? [
            { text: inquiry.invoiceAddress?.name || '', bold: true },
            inquiry.invoiceAddress?.contactPerson || '',
            inquiry.invoiceAddress?.address || '',
            `${inquiry.invoiceAddress?.postalCode} ${inquiry.invoiceAddress?.city} (${inquiry.invoiceAddress?.country})`,
          ]
        : [$t('Invoice address is missing')]),
    ],
    headInfoContent: [
      [
        ['OfferDate', inquiry.offerDate && inquiry.offerDate.split(',')[0]],
        ['Offer Id', inquiry.inqTitle || inquiry.id],
        ['Project Reference', inquiry.projectId],
        ['Date of expiry', inquiry.dateOfExpiry],
        ['Responsible Operator', inquiry.operatorFullName],
        ['-', '-'],
        ['Payment condition', $t(inquiry.paymentCondition!)],
        [
          'Delivery Options',
          inquiry.deliveryOption?.name?.includes?.('please select') ? '' : $t(inquiry.deliveryOption?.name || ''),
        ],
        ['Delivery Address', inquiry.deliveryAddress],
      ]
        .filter(([, value]) => !!value)
        .map(([title, value]) =>
          value === '-'
            ? { text: '', margin: [0, 6, 0, 0] }
            : {
                columns: [
                  { text: $t(title as string) + ': ', bold: true },
                  { text: value as string, alignment: 'right' },
                ],
              },
        ),
      {
        stack: [
          {
            text: inquiry.deliveryAddress
              ? [
                  [
                    inquiry.deliveryAddress?.name,
                    inquiry.deliveryAddress?.contactPerson,
                    inquiry.deliveryAddress?.address,
                  ]
                    .filter((v) => !!v)
                    .join(', '),
                  ', ',
                  inquiry.deliveryAddress?.postalCode || '',
                  ' ',
                  inquiry.deliveryAddress?.city || '',
                  ' ',
                  `(${inquiry.deliveryAddress?.country})`,
                ]
              : [],
          },
        ],
      },
    ],
    itemTableConfig: {
      colConfigs: tableConfig.filter((configItem) => !configItem.id || !hideFields!.includes(configItem.id)),
      rowsOrGroups: createTableRows(inquiry.items),
    },
    tableFooter: isInquiryStatusAfterOffered
      ? footerItemsAfterOffered(inquiry, hasReachedDiscountLimit)
      : footerItemsBeforeOffered(inquiry),
    belowTableContent: [
      convertToPlain(inquiry.offerTexts.replaceAll('<br>', '\n')) || '',
      '\n',
      $t('We are looking forward to working with you.'),
    ],
    pageFooter: {
      columns: [
        {
          text: 'nuucon GmbH \n Jarrestraße 42, 22303 Hamburg \n www.nuucon.com',
        },
        {
          text: 'Managing Directors \n Pierre Haarfeld and Philipp Renerig \n T+49 40 228 582 10-0 \n business@nuucon.com',
        },
        { text: 'Ust-ID: DE 313871675 \n HRB-Nr: 147877 \n Amtsgericht Hamburg' },
        { text: '' },
      ],
    },
  }
  return createDocumentDefinition(omit<any, any>(pdfInfoInquiry, hideFields) as any)
}
